import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Users, Wrench, Check, Ban, AlertTriangle, Activity,
  Bot, Wifi, WifiOff, RefreshCw, Settings,
  Server, Globe, Download, Gift, BarChart3
} from "lucide-react";
import { toast } from "sonner";

const BotStatusCard = ({ bot, index, onFix, fixing }) => {
  const statusConfig = {
    ok: { color: "bg-green-500", text: "text-green-500", label: "En ligne", icon: Wifi },
    warning: { color: "bg-yellow-500", text: "text-yellow-500", label: "Attention", icon: AlertTriangle },
    error: { color: "bg-red-500", text: "text-red-500", label: "Erreur", icon: AlertTriangle },
    down: { color: "bg-red-600", text: "text-red-500", label: "Hors ligne", icon: WifiOff },
    unreachable: { color: "bg-gray-500", text: "text-gray-500", label: "Injoignable", icon: WifiOff },
    no_token: { color: "bg-gray-500", text: "text-gray-500", label: "Non configuré", icon: Ban },
  };

  const cfg = statusConfig[bot.status] || statusConfig.down;
  const StatusIcon = cfg.icon;

  return (
    <Card className="bg-card border-border" data-testid={`bot-status-${index}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bot className="h-8 w-8 text-primary" />
              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${cfg.color} border-2 border-card`} />
            </div>
            <div>
              <p className="font-semibold text-sm">{bot.name}</p>
              <p className="text-xs text-muted-foreground font-mono">{bot.handle}</p>
            </div>
          </div>
          <Badge variant="outline" className={`${cfg.text} border-current`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {cfg.label}
          </Badge>
        </div>

        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Message</span>
            <span className={bot.status === "ok" ? "text-green-500" : "text-destructive"}>{bot.message}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">En attente</span>
            <span className={bot.pending_updates > 10 ? "text-yellow-500 font-bold" : ""}>{bot.pending_updates}</span>
          </div>
          {bot.webhook_url && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Webhook</span>
              <span className="text-primary truncate max-w-[200px]" title={bot.webhook_url}>
                {bot.webhook_url.replace("https://", "").split("/api/")[0]}
              </span>
            </div>
          )}
          {bot.last_error && bot.status !== "ok" && (
            <div className="mt-2 p-2 bg-destructive/10 rounded-sm">
              <p className="text-destructive text-xs">{bot.last_error}</p>
              {bot.last_error_date && (
                <p className="text-muted-foreground text-xs mt-1">
                  {new Date(bot.last_error_date).toLocaleString("fr-FR")}
                </p>
              )}
            </div>
          )}
        </div>

        {bot.status !== "ok" && bot.status !== "no_token" && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-3 text-xs"
            onClick={() => onFix(index)}
            disabled={fixing}
            data-testid={`fix-bot-${index}`}
          >
            <Settings className="h-3 w-3 mr-1" />
            {fixing ? "Réparation..." : "Réparer le webhook"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export const DashboardOverview = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    active_users: 0,
    blocked_users: 0,
    expired_users: 0,
    online_users: 0,
    total_tools: 0,
    total_providers: 0,
    providers_active: 0,
    total_hosts: 0,
    total_firestick: 0,
    firestick_active: 0,
    total_gift_codes: 0,
    gift_codes_unused: 0,
    tool_uses_today: 0,
  });
  const [bots, setBots] = useState([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const { token } = useAuth();

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats");
    }
  };

  const fetchBots = async () => {
    setBotsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/monitoring/bots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBots(response.data);
    } catch (error) {
      console.error("Error fetching bot status");
    } finally {
      setBotsLoading(false);
    }
  };

  const handleFix = async (index) => {
    setFixing(true);
    try {
      const res = await axios.post(`${API}/admin/monitoring/bots/fix-webhook/${index}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(`Webhook réparé : ${res.data.webhook_url}`);
        setTimeout(fetchBots, 2000);
      } else {
        toast.error(res.data.description || "Échec de la réparation");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setFixing(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchBots();
    const statsInterval = setInterval(fetchStats, 30000);
    const botsInterval = setInterval(fetchBots, 60000); // Check bots every 60s
    return () => {
      clearInterval(statsInterval);
      clearInterval(botsInterval);
    };
  }, [token]);

  const statCards = [
    { label: "Total Utilisateurs", value: stats.total_users, icon: Users, color: "text-foreground" },
    { label: "En ligne", value: stats.online_users, icon: Activity, color: "text-emerald-400", pulse: stats.online_users > 0 },
    { label: "Actifs", value: stats.active_users, icon: Check, color: "text-primary" },
    { label: "Bloqués", value: stats.blocked_users, icon: Ban, color: "text-destructive" },
    { label: "Expirés", value: stats.expired_users, icon: AlertTriangle, color: "text-orange-400" },
    { label: "Outils", value: stats.total_tools, icon: Wrench, color: "text-accent" },
  ];

  const proStatCards = [
    { label: "Fournisseurs", value: stats.total_providers, sub: `${stats.providers_active} actifs`, icon: Globe, color: "text-blue-400" },
    { label: "Total Hosts", value: stats.total_hosts, icon: Server, color: "text-cyan-400" },
    { label: "Codes Downloader", value: stats.total_firestick, sub: `${stats.firestick_active} actifs`, icon: Download, color: "text-violet-400" },
    { label: "Gift Codes", value: stats.total_gift_codes, sub: `${stats.gift_codes_unused} disponibles`, icon: Gift, color: "text-amber-400" },
    { label: "Utilisations Outils", value: stats.tool_uses_today, sub: "aujourd'hui", icon: BarChart3, color: "text-rose-400" },
  ];

  return (
    <div className="p-6 animate-fade-in" data-testid="dashboard-overview">
      <h1 className="text-3xl font-bold tracking-tight mb-6">Dashboard</h1>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-card border-border hover:border-primary/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{stat.label}</p>
                  <div className="flex items-center gap-2">
                    <p className={`text-3xl font-bold font-mono ${stat.color}`}>{stat.value}</p>
                    {stat.pulse && (
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                      </span>
                    )}
                  </div>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pro Statistics */}
      <Card className="bg-card border-border mb-8" data-testid="pro-stats">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-primary" />
            Statistiques Pro
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {proStatCards.map((stat, index) => (
              <div key={index} className="relative overflow-hidden rounded-lg border border-border bg-background p-4 hover:border-primary/30 transition-colors" data-testid={`pro-stat-${index}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{stat.label}</p>
                    <p className={`text-3xl font-bold font-mono ${stat.color}`}>{stat.value}</p>
                    {stat.sub && (
                      <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
                    )}
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color} opacity-30`} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bot Monitoring Section */}
      <Card className="bg-card border-border mb-8">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              Monitoring Bots Telegram
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchBots}
              disabled={botsLoading}
              data-testid="refresh-bots"
            >
              <RefreshCw className={`h-4 w-4 ${botsLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {bots.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bots.map((bot, i) => (
                <BotStatusCard key={i} bot={bot} index={i} onFix={handleFix} fixing={fixing} />
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              {botsLoading ? "Vérification en cours..." : "Aucun bot configuré"}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Accès Rapide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/admin/users">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <Users className="h-5 w-5 mr-3 text-primary" />
                <div className="text-left">
                  <p className="font-medium">Gérer les Utilisateurs</p>
                  <p className="text-xs text-muted-foreground">Créer, modifier, bloquer</p>
                </div>
              </Button>
            </Link>
            <Link to="/admin/tools">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <Wrench className="h-5 w-5 mr-3 text-accent" />
                <div className="text-left">
                  <p className="font-medium">Gérer les Outils</p>
                  <p className="text-xs text-muted-foreground">Ajouter, activer, désactiver</p>
                </div>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
